import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, CheckCircle2, Clock, Users, FileCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1772112334845-86016056137b?w=1200&q=85';

export default function RenovationCompletePage() {
  const navigate = useNavigate();

  const included = [
    'Démolition et évacuation',
    'Électricité et plomberie',
    'Cloisonnement et isolation',
    'Revêtements (peinture, parquet, carrelage)',
    'Cuisine et salle de bain',
    'Finitions et détails'
  ];

  const piloted = [
    'Coordination de 5 à 8 corps de métier',
    'Planning global avec jalons',
    'Suivi hebdomadaire d\'avancement',
    'Contrôles qualité par phase',
    'Gestion des imprévus',
    'Réception finale'
  ];

  const steps = [
    { title: 'Diagnostic', desc: 'Visite complète, analyse structurelle, plan d\'action', duration: '1 semaine' },
    { title: 'Devis détaillé', desc: 'Chiffrage par poste, planning prévisionnel', duration: '3-5 jours' },
    { title: 'Démolition', desc: 'Mise à nu, évacuation, préparation', duration: '1-2 semaines' },
    { title: 'Gros œuvre', desc: 'Électricité, plomberie, cloisonnement', duration: '3-5 semaines' },
    { title: 'Second œuvre', desc: 'Revêtements, cuisine, SDB', duration: '3-4 semaines' },
    { title: 'Finitions', desc: 'Peinture, détails, nettoyage', duration: '1-2 semaines' }
  ];

  const faqs = [
    {
      q: 'Combien de temps dure une rénovation complète ?',
      a: 'Entre 8 et 16 semaines selon la surface et la complexité. Nous vous donnons un planning précis dès le devis.'
    },
    {
      q: 'Puis-je habiter pendant les travaux ?',
      a: 'Non, pour une rénovation complète, le logement doit être libéré. Nous organisons le chantier pour minimiser la durée.'
    },
    {
      q: 'Que se passe-t-il si on découvre un problème caché ?',
      a: 'Nous vous informons immédiatement, proposons une solution, et ajustons le planning et le budget de manière transparente.'
    },
    {
      q: 'Les meubles et équipements sont-ils inclus ?',
      a: 'Nous pouvons piloter la fourniture et pose de cuisine, SDB, etc. Tout est explicité dans le devis.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge 
                className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase"
                style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
              >
                Service premium
              </Badge>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold leading-tight"
                style={{ color: 'var(--hr-text)' }}
              >
                Rénovation complète pilotée
              </h1>
              
              <p className="text-xl leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                Transformation totale de votre intérieur. Nous coordonnons tout, du sol au plafond.
              </p>

              <div className="flex gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-xl px-8"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/devis')}
                >
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-xl px-8"
                  onClick={() => navigate('/suivi-de-chantier')}
                >
                  Voir notre pilotage
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>8-16 semaines</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>5-8 artisans</span>
                </div>
              </div>
            </div>

            <div className="media-frame">
              <img src={HERO_IMAGE} alt="Rénovation complète" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* LE PROBLÈME */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-center mb-12" style={{ color: 'var(--hr-text)' }}>
              Une rénovation complète sans pilotage, c'est le chaos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                '10 artisans qui ne se parlent pas',
                'Un planning qui explose de 3 mois',
                'Des choix à faire sans conseil',
                'Des finitions bâclées',
                'Un budget qui dérape',
                'Un résultat décevant'
              ].map((pain, i) => (
                <div key={pain} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--hr-bg)' }}>
                  <div className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--hr-danger)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text)' }}>{pain}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CE QUI EST INCLUS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Ce qui est inclus
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>Transformation complète de A à Z</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {included.map((item) => (
              <Card key={item} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-success)' }} />
                  <span className="text-base font-medium" style={{ color: 'var(--hr-text)' }}>{item}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUI EST PILOTÉ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide mb-4" style={{ color: 'var(--hr-accent)' }}>
              Notre différence
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Ce que nous pilotons pour vous
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {piloted.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardContent className="p-6 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                      <span className="text-white text-sm font-bold">{i + 1}</span>
                    </div>
                    <span className="text-base" style={{ color: 'var(--hr-text)' }}>{item}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DÉROULÉ */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Comment ça se déroule
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>6 étapes cadrées, pas d'improvisation</p>
          </div>
          <div className="space-y-4">
            {steps.map((step, i) => (
              <Card key={step.title} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-center gap-6">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                    <span className="text-white font-bold text-lg">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Figtree'] text-lg font-semibold mb-1" style={{ color: 'var(--hr-text)' }}>{step.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>{step.desc}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium" style={{ color: 'var(--hr-accent)' }}>{step.duration}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Questions fréquentes
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem 
                key={faq.q} 
                value={`item-${i}`}
                className="rounded-2xl border px-6" 
                style={{ borderColor: 'var(--hr-border)', backgroundColor: 'var(--hr-bg)' }}
              >
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-['Figtree'] font-semibold" style={{ color: 'var(--hr-text)' }}>{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm leading-relaxed pt-2" style={{ color: 'var(--hr-text-muted)' }}>{faq.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-primary-fg)' }}>
            Prêt à transformer votre intérieur ?
          </h2>
          <p className="text-lg" style={{ color: 'rgba(245,243,239,0.9)' }}>
            Recevez un devis détaillé et un planning précis. Sans engagement.
          </p>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
          >
            Demander mon devis
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}